/**
 * Whether this Playwright supports `snapshotPath(name, { kind })`.
 *
 * The options form arrived in Playwright 1.51. Before that `snapshotPath` took
 * variadic path segments, so an options object is consumed as a segment and
 * the resolved path is wrong — and because both call sites sit inside a
 * `try`/`catch` that swallows everything, DOM comparison and the
 * passing-baseline attachment simply never worked and said nothing about it.
 *
 * The package's peer range is `>=1.35.0` and stays that way deliberately:
 * everything except these two visual extras works on the older versions, and
 * raising the floor would lock those users out of the whole reporter over one
 * feature. So the loss is detected and *announced* once, instead of being
 * silent.
 *
 * The check is a feature probe rather than a version comparison. Reading
 * `@playwright/test/package.json` meant resolving a module at runtime from a
 * file that has to stay importable from both the CJS and the ESM build, and
 * the only spelling that worked in both reached the module loader through a
 * dynamically evaluated string. The repo's payload scanner rejects that shape
 * on sight, and rightly — this package is published to npm, and the scanner
 * does not exempt code that means well. Asking the API what it does needs no
 * module resolution, no dynamic execution and no version table, and it tests
 * the behaviour actually depended on rather than a number that correlates
 * with it.
 */

/**
 * The one method probed. Both call sites already hold a `testInfo`; this is the
 * narrowest shape that covers the two call forms.
 */
export interface SnapshotPathCapable {
  snapshotPath(name: string, options: { kind: 'screenshot' }): string;
}

/**
 * Passed to the probe. Never written to disk — `snapshotPath` only computes a
 * path — and named so it is unmistakable in a stack trace or a log line.
 */
const PROBE_NAME = '__testrelic_snapshot_path_probe__.png';

/** Printed at most once per process, however many tests hit the path. */
const WARNED_KEY = Symbol.for('testrelic.snapshotPathWarned');

interface WarnSlot {
  warned: boolean;
}

/**
 * The latch lives on `globalThis`, not in a module variable.
 *
 * tsup builds with `splitting: false`, so every entry point bundles its own
 * copy of this module — and this module is reached from five of them. A
 * module-level `let` would therefore be five separate latches, and the
 * "at most once per process" promise above would print up to five times. This
 * is the same fix, and the same reason, as the active context in
 * `assertion-tracker.ts`.
 */
function warnSlot(): WarnSlot {
  const g = globalThis as unknown as Record<symbol, WarnSlot | undefined>;
  let slot = g[WARNED_KEY];
  if (!slot) {
    slot = { warned: false };
    g[WARNED_KEY] = slot;
  }
  return slot;
}

/**
 * True when `snapshotPath(name, { kind })` resolves the way this code needs.
 *
 * The probe asks what the OLD form would have done wrong, not what the new one
 * returns verbatim. Before 1.51 the options object is consumed as another path
 * segment: `path.join` throws on a non-string, and a version that coerced it
 * instead would stringify it into the path as `[object Object]`. Either shape
 * answers `false`.
 *
 * It deliberately does NOT check that the result ends with the name it was
 * given. It did, and that was wrong: `snapshotPathTemplate` legitimately
 * inserts the project and platform before the extension, so
 * `toHaveScreenshot('dashboard.png')` resolves to `dashboard-win32.png` on
 * every default Playwright project. The probe therefore answered `false`
 * everywhere and silently disabled BOTH of its callers — passing-baseline
 * capture and DOM comparison — which shipped in 2.16.0 and is why a green
 * visual run recorded no comparisons at all and no element changes ever
 * appeared. Never assert on the shape of a path a template owns.
 */
export function supportsSnapshotPathOptions(testInfo: SnapshotPathCapable): boolean {
  try {
    const resolved = testInfo.snapshotPath(PROBE_NAME, { kind: 'screenshot' });
    return typeof resolved === 'string' && !resolved.includes('[object Object]');
  } catch {
    return false;
  }
}

/**
 * Say once that a visual extra is unavailable on this Playwright, and why.
 *
 * `feature` names what the user loses, so the line is actionable rather than
 * an abstract version complaint.
 */
export function warnSnapshotPathUnsupported(feature: string): void {
  const slot = warnSlot();
  if (slot.warned) return;
  slot.warned = true;
  process.stderr.write(
    `[testrelic] ${feature} needs Playwright >= 1.51 for ` +
      `testInfo.snapshotPath(name, { kind }); this project's Playwright ` +
      `resolves that call the older way. Everything else in the reporter is ` +
      `unaffected — upgrade Playwright to enable it.\n`,
  );
}

/** Reset the once-per-process latch. Test-only. */
export function resetSnapshotPathWarning(): void {
  warnSlot().warned = false;
}
